import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, Inbox } from 'lucide-react';
import {
  staffMemberDocuments,
  staffDocumentRequests,
  type OnboardingWorkflowGroup,
} from '../../services/staffDocumentService';
import { toastService } from '../../services/toastService';
import OnBehalfModal from './OnBehalfModal';
import { RubyLoader } from '../RubyLoader';

import { RubyGemMark } from '../RubyGemMark';
// Port of platform-app components/team/EmployeeProgressPanel.tsx for the
// tenant-less staff-service (RUBY light chrome). `tenantId` is accepted for
// prop-contract compatibility but ignored.

type WorkflowStep = OnboardingWorkflowGroup['steps'][number];

interface OnBehalfState {
  request: {
    id: string;
    templateName: string;
    templateType?: string;
    documentTemplateId?: string;
    userId: string;
    formFields?: any[];
  };
  mode: 'complete' | 'attach';
}

// =============================================================================
// PURE HELPER
// =============================================================================

/**
 * Roll up a workflow's step statuses into a progress summary.
 * - done   = steps whose status is 'verified' OR 'submitted'
 * - total  = number of steps
 * - percent = rounded percentage (0 when no steps)
 * - allDone = true only when there is at least one step and every step is done
 */
export function summarizeWorkflow(
  steps: { status: string }[]
): { done: number; total: number; percent: number; allDone: boolean } {
  const total = steps.length;
  const done = steps.filter(
    (s) => s.status === 'verified' || s.status === 'submitted'
  ).length;
  const percent = total ? Math.round((done / total) * 100) : 0;
  const allDone = total > 0 && done === total;
  return { done, total, percent, allDone };
}

// =============================================================================
// DISPLAY HELPERS
// =============================================================================

const formatDate = (ms?: number): string => {
  if (!ms) return '';
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

type StepStatus = OnboardingWorkflowGroup['steps'][number]['status'];

const STEP_ICON: Record<StepStatus, { glyph: string; className: string }> = {
  verified: { glyph: '✔', className: 'text-emerald-600' },
  submitted: { glyph: '⏳', className: 'text-blue-600' },
  pending: { glyph: '○', className: 'text-stone-400' },
  rejected: { glyph: '✕', className: 'text-red-600' },
};

const STEP_BADGE: Record<StepStatus, { label: string; className: string }> = {
  verified: { label: 'Verified', className: 'bg-emerald-100 text-emerald-800' },
  submitted: { label: 'In review', className: 'bg-blue-100 text-blue-800' },
  pending: { label: 'Pending', className: 'bg-amber-100 text-amber-800' },
  rejected: { label: 'Rejected', className: 'bg-red-100 text-red-800' },
};

// =============================================================================
// COMPONENT
// =============================================================================

interface EmployeeProgressPanelProps {
  /** Ignored (tenant-less staff-service); kept so page ports stay mechanical. */
  tenantId?: string;
  employee: { id: string; name: string };
  /** Bump this from the parent to force a re-fetch (e.g. after assigning a workflow). */
  refreshKey?: number;
}

export default function EmployeeProgressPanel({
  employee,
  refreshKey,
}: EmployeeProgressPanelProps) {
  const [workflows, setWorkflows] = useState<OnboardingWorkflowGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [onBehalf, setOnBehalf] = useState<OnBehalfState | null>(null);
  // Internal refresh counter — the panel can't mutate its own `refreshKey` prop,
  // so we bump this after an on-behalf action to re-run the fetch effect.
  const [localRefresh, setLocalRefresh] = useState(0);
  const [busyVerify, setBusyVerify] = useState<string | null>(null);

  // Approve (verify) or reject a submitted step, then refresh the panel.
  const handleVerify = async (requestId: string, approved: boolean) => {
    setBusyVerify(requestId);
    try {
      await staffDocumentRequests.verify(requestId, approved ? 'verify' : 'reject');
      setLocalRefresh((n) => n + 1);
    } catch (err: any) {
      toastService.error(err?.message || 'Failed to update the step. Please try again.');
    } finally {
      setBusyVerify(null);
    }
  };

  // Stale-guard: ignore responses from superseded requests.
  const seqRef = useRef(0);

  useEffect(() => {
    let active = true;
    const seq = ++seqRef.current;

    setLoading(true);
    setError(null);

    staffMemberDocuments
      .getOnboardingStatus(employee.id)
      .then((status) => {
        // Drop out-of-order / stale responses.
        if (!active || seq !== seqRef.current) return;
        setWorkflows(Array.isArray(status?.workflows) ? status.workflows : []);
        setLoading(false);
      })
      .catch((err: any) => {
        if (!active || seq !== seqRef.current) return;
        setError(err?.message || 'Failed to load progress.');
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [employee.id, refreshKey, localRefresh]);

  const buildRequest = (step: WorkflowStep): OnBehalfState['request'] => ({
    id: step.requestId,
    templateName: step.templateName,
    templateType: step.templateType,
    documentTemplateId: step.documentTemplateId,
    userId: employee.id,
    formFields: step.formFields,
  });

  // ---- Loading ----
  if (loading) {
    return (
      <RubyLoader variant="section" label="Loading progress…" className="py-10" />
    );
  }

  // ---- Error ----
  if (error) {
    return (
      <div className="bg-white rounded-xl border border-red-200 shadow-sm p-4 flex items-start gap-2">
        <AlertCircle size={16} className="text-red-600 mt-0.5 shrink-0" />
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  // ---- Empty ----
  if (workflows.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6 text-center">
        <Inbox size={24} className="text-stone-400 mx-auto mb-2" />
        <p className="text-sm font-medium text-stone-900">No workflows assigned yet</p>
        <p className="text-xs text-stone-500 mt-1">
          Assign a workflow to {employee.name} to start tracking progress.
        </p>
      </div>
    );
  }

  // ---- Workflow cards ----
  return (
    <>
    <div className="space-y-3">
      {workflows.map((wf, wfIdx) => {
        const summary = summarizeWorkflow(wf.steps);
        const chip = summary.allDone
          ? { label: 'Completed', className: 'bg-emerald-100 text-emerald-800' }
          : { label: 'In progress', className: 'bg-amber-100 text-amber-800' };

        return (
          <div
            key={wf.assignmentId || `${wf.onboardingTemplateId}-${wfIdx}`}
            className="bg-white rounded-xl border border-stone-200 shadow-sm p-4"
          >
            {/* Header */}
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-stone-900">{wf.name}</h3>
              <span
                className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${chip.className}`}
              >
                {chip.label}
              </span>
            </div>

            {/* Sub-line */}
            <p className="text-[11px] text-stone-500 mt-0.5">
              {wf.assignedAt ? `Assigned ${formatDate(wf.assignedAt)} · ` : ''}
              {summary.done} of {summary.total} done
            </p>

            {/* Progress bar */}
            <div className="h-2 bg-stone-200 rounded-full mt-2 overflow-hidden">
              <div
                className="h-full bg-admin-primary transition-all"
                style={{ width: `${summary.percent}%` }}
              />
            </div>

            {/* Step rows */}
            {wf.steps.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {wf.steps.map((step) => {
                  const icon = STEP_ICON[step.status] || STEP_ICON.pending;
                  const badge = STEP_BADGE[step.status] || STEP_BADGE.pending;
                  return (
                    <div
                      key={step.requestId}
                      className="flex items-center gap-2 text-sm"
                    >
                      <span className={icon.className}>{icon.glyph}</span>
                      <span className="flex-1 text-stone-700">
                        {step.templateName}
                      </span>

                      {/* Due date for pending steps */}
                      {step.status === 'pending' && step.dueDate && (
                        <span className="text-[11px] text-stone-500">
                          Due {formatDate(step.dueDate)}
                        </span>
                      )}

                      {/* "by admin" chip when an admin submitted on behalf */}
                      {step.submittedByAdmin && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                          by admin
                        </span>
                      )}

                      {/* "Result attached" chip */}
                      {step.resultFileKey && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">
                          Result attached
                        </span>
                      )}

                      {/* Status badge */}
                      <span
                        className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${badge.className}`}
                      >
                        {badge.label}
                      </span>

                      {/* Approve / Reject for steps awaiting review (submitted) */}
                      {step.status === 'submitted' && (
                        <>
                          <button
                            type="button"
                            disabled={busyVerify === step.requestId}
                            onClick={() => handleVerify(step.requestId, true)}
                            className="text-[11px] font-semibold px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1"
                          >
                            {busyVerify === step.requestId ? <RubyGemMark size={11} drawing color="currentColor" /> : null}
                            Approve
                          </button>
                          <button
                            type="button"
                            disabled={busyVerify === step.requestId}
                            onClick={() => handleVerify(step.requestId, false)}
                            className="text-[11px] font-medium px-2 py-0.5 rounded border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50"
                          >
                            Reject
                          </button>
                        </>
                      )}

                      {/* On-behalf actions */}
                      {step.status === 'pending' && (
                        <button
                          type="button"
                          onClick={() =>
                            setOnBehalf({ request: buildRequest(step), mode: 'complete' })
                          }
                          className="text-[11px] font-medium px-2 py-0.5 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        >
                          Complete on behalf
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() =>
                          setOnBehalf({ request: buildRequest(step), mode: 'attach' })
                        }
                        className="text-[11px] font-medium px-2 py-0.5 rounded border border-stone-300 text-stone-600 hover:bg-stone-50"
                      >
                        Attach
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>

    {onBehalf && (
      <OnBehalfModal
        request={onBehalf.request}
        employeeName={employee.name}
        mode={onBehalf.mode}
        onClose={() => setOnBehalf(null)}
        onDone={() => {
          setOnBehalf(null);
          setLocalRefresh((n) => n + 1);
        }}
      />
    )}
    </>
  );
}
